import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import api, { formatApiError } from '../../lib/api';
import { toast } from 'sonner';

const EMPTY = {
  code: '',
  company_name: '',
  trading_as: '',
  registration_number: '',
  vat_number: '',
  contact_person: '',
  phone: '',
  email: '',
  physical_address: '',
  postal_address: '',
  payment_terms_days: 30,
  po_required: false,
  bill_minimum_units_to_client: false,
  cancel_billing_window_minutes: 15,
  pricing: [],
  notes: '',
  sop: '',
};

export const CustomerDialog = ({ open, onOpenChange, customer, services, onSaved }) => {
  const isEdit = Boolean(customer?.id);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(customer ? {
        ...EMPTY,
        ...customer,
        pricing: customer.pricing || [],
      } : EMPTY);
    }
  }, [open, customer]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const upsertPricing = (service_id, field, value) => {
    setForm((s) => {
      const existing = s.pricing.find((p) => p.service_id === service_id);
      const numeric = field === 'rate' || field === 'minimum_units' ? Number(value || 0) : value;
      if (!existing) {
        if (!numeric) return s;
        return { ...s, pricing: [...s.pricing, { service_id, rate: 0, minimum_units: 0, [field]: numeric }] };
      }
      return {
        ...s,
        pricing: s.pricing.map((p) =>
          p.service_id === service_id ? { ...p, [field]: numeric } : p
        ).filter((p) => p.rate || p.minimum_units),
      };
    });
  };

  const priceFor = (service_id, field) => {
    const found = form.pricing.find((p) => p.service_id === service_id);
    return found ? found[field] : '';
  };

  const handleSave = async () => {
    if (!form.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // strip empty optionals
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' && k !== 'company_name') delete payload[k];
      });
      payload.pricing = (payload.pricing || []).filter((p) => p.rate > 0);

      if (isEdit) {
        await api.patch(`/customers/${customer.id}`, payload);
        toast.success('Customer updated');
      } else {
        await api.post('/customers', payload);
        toast.success('Customer created');
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#0D5B69]">
            {isEdit ? 'Edit Customer' : 'New Customer'}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? `Updating ${customer.company_name}` : 'Add a new client to the system'}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="details" className="mt-2">
          <TabsList>
            <TabsTrigger value="details" data-testid="tab-details">Company Details</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing">Billing Rules</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing">Service Pricing</TabsTrigger>
            <TabsTrigger value="sop" data-testid="tab-sop">SOP</TabsTrigger>
          </TabsList>

          {/* DETAILS */}
          <TabsContent value="details" className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Customer Code" testid="code">
                <Input value={form.code || ''} onChange={(e) => set('code', e.target.value)} placeholder="DISC-001" />
              </Field>
              <Field label="Company Name *" testid="company-name">
                <Input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} required />
              </Field>
              <Field label="Trading As" testid="trading-as">
                <Input value={form.trading_as || ''} onChange={(e) => set('trading_as', e.target.value)} />
              </Field>
              <Field label="Registration No" testid="reg-no">
                <Input value={form.registration_number || ''} onChange={(e) => set('registration_number', e.target.value)} />
              </Field>
              <Field label="VAT Number" testid="vat-no">
                <Input value={form.vat_number || ''} onChange={(e) => set('vat_number', e.target.value)} />
              </Field>
              <Field label="Contact Person" testid="contact-person">
                <Input value={form.contact_person || ''} onChange={(e) => set('contact_person', e.target.value)} />
              </Field>
              <Field label="Phone" testid="phone">
                <Input value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} placeholder="+27 ..." />
              </Field>
              <Field label="Email" testid="email">
                <Input type="email" value={form.email || ''} onChange={(e) => set('email', e.target.value)} />
              </Field>
            </div>
            <Field label="Physical Address" testid="physical-address">
              <Textarea rows={2} value={form.physical_address || ''} onChange={(e) => set('physical_address', e.target.value)} />
            </Field>
            <Field label="Postal Address" testid="postal-address">
              <Textarea rows={2} value={form.postal_address || ''} onChange={(e) => set('postal_address', e.target.value)} />
            </Field>
            <Field label="Notes" testid="notes">
              <Textarea rows={2} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} placeholder="Internal notes — not shown to client" />
            </Field>
          </TabsContent>

          {/* BILLING */}
          <TabsContent value="billing" className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Payment Terms (days)" testid="payment-terms">
                <Input type="number" min={0} value={form.payment_terms_days}
                  onChange={(e) => set('payment_terms_days', Number(e.target.value || 0))} />
              </Field>
              <Field label="Cancel Billing Window (minutes)" testid="cancel-window"
                hint="Within this window after the case is logged, a cancellation incurs no bill (spec A4)">
                <Input type="number" min={0} value={form.cancel_billing_window_minutes}
                  onChange={(e) => set('cancel_billing_window_minutes', Number(e.target.value || 0))} />
              </Field>
            </div>

            <ToggleRow
              label="Bill Minimum Units to Client"
              hint="If enabled, the client is billed at least the service's minimum hours / units (spec A3)"
              checked={form.bill_minimum_units_to_client}
              onChange={(v) => set('bill_minimum_units_to_client', v)}
              testid="bill-minimum"
            />
            <ToggleRow
              label="PO Number Required"
              hint="If enabled, incidents and statements must include a PO number for this client"
              checked={form.po_required}
              onChange={(v) => set('po_required', v)}
              testid="po-required"
            />
          </TabsContent>

          {/* PRICING */}
          <TabsContent value="pricing" className="space-y-3 mt-4">
            <p className="text-xs text-slate-500 mb-2">
              Override the default service rate for this customer. Leave blank to use the default.
              All rates are <strong>excluding VAT</strong>.
            </p>
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left p-2.5 font-semibold">Service</th>
                    <th className="text-left p-2.5 font-semibold">Default Rate</th>
                    <th className="text-left p-2.5 font-semibold">Client Rate (ex VAT)</th>
                    <th className="text-left p-2.5 font-semibold">Min Units</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((svc) => (
                    <tr key={svc.id} className="border-t border-slate-100">
                      <td className="p-2.5">
                        <p className="font-medium text-slate-900">{svc.name}</p>
                        <p className="text-xs text-slate-500">{svc.billing_type.replace('_', ' ')}</p>
                      </td>
                      <td className="p-2.5 text-slate-500 font-mono text-xs">
                        R {svc.default_rate.toFixed(2)}
                      </td>
                      <td className="p-2.5">
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          className="h-8"
                          value={priceFor(svc.id, 'rate')}
                          onChange={(e) => upsertPricing(svc.id, 'rate', e.target.value)}
                          placeholder="—"
                          data-testid={`pricing-rate-${svc.code}`}
                        />
                      </td>
                      <td className="p-2.5">
                        <Input
                          type="number"
                          min={0}
                          step="0.25"
                          className="h-8"
                          value={priceFor(svc.id, 'minimum_units')}
                          onChange={(e) => upsertPricing(svc.id, 'minimum_units', e.target.value)}
                          placeholder="—"
                          data-testid={`pricing-min-${svc.code}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <BillingPreview services={services} pricing={form.pricing} customer={form} />
          </TabsContent>

          {/* SOP — Standard Operating Procedure (free text) */}
          <TabsContent value="sop" className="space-y-3 mt-4" data-testid="sop-tab-content">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Standard Operating Procedure</Label>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Free-text instructions specific to this client — escalation paths, contact protocols, what triggers a call-out, special handling, billing exceptions, etc. Operators see this on every case for this customer.
                </p>
              </div>
              <span className="text-[10px] text-slate-400 shrink-0 mt-1 font-mono">{(form.sop || '').length} chars</span>
            </div>
            <Textarea
              rows={16}
              value={form.sop || ''}
              onChange={(e) => set('sop', e.target.value)}
              placeholder={`e.g.
1. On panic activation, dispatch the closest contractor within 5 minutes.
2. Notify Mr Smith on +27 …
3. SAPS escalation only after 15 min with no contact …`}
              className="font-mono text-xs leading-relaxed"
              data-testid="customer-sop-input"
            />
            <p className="text-[11px] text-slate-400">
              Whitespace and line breaks are preserved when rendered to operators.
            </p>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-btn">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold"
            data-testid="save-customer-btn"
          >
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Customer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, hint, children, testid }) => (
  <div className="space-y-1" data-testid={`field-${testid}`}>
    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-slate-400 italic">{hint}</p>}
  </div>
);

const ToggleRow = ({ label, hint, checked, onChange, testid }) => (
  <div className="flex items-start gap-3 p-3 border border-slate-200 rounded-md hover:bg-slate-50" data-testid={`toggle-${testid}`}>
    <Switch checked={checked} onCheckedChange={onChange} className="mt-0.5" />
    <div>
      <Label className="text-sm font-medium text-slate-900">{label}</Label>
      {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
    </div>
  </div>
);

/**
 * BillingPreview — live "what-if" calculator showing what CSG will charge this client
 * for a sample incident duration. Honours per-customer rate override + minimum-units flag.
 * Read-only, computed entirely in the browser.
 */
const BillingPreview = ({ services, pricing, customer }) => {
  const VAT_RATE = 0.15;
  const billMin = !!customer.bill_minimum_units_to_client;

  // pick first 3 services with explicit pricing OR fall back to first 3 services
  const overrideMap = new Map((pricing || []).map((p) => [p.service_id, p]));
  const previews = (services || []).slice(0, 3).map((svc) => {
    const ovr = overrideMap.get(svc.id);
    const rate = ovr?.rate ?? svc.default_rate;
    const customerMin = ovr?.minimum_units ?? 0;
    const minUnits = Math.max(customerMin, billMin ? svc.minimum_units : 0);
    return { svc, rate, minUnits, isOverride: Boolean(ovr?.rate) };
  });

  // duration presets in hours
  const samples = [0.25, 0.5, 1, 2];

  const compute = (rate, billing_type, hours, minUnits) => {
    let units = hours;
    if (billing_type === 'fixed') units = 1;
    if (minUnits > 0 && units < minUnits) units = minUnits;
    const subtotal = rate * units;
    const vat = Math.round(subtotal * VAT_RATE * 100) / 100;
    const total = Math.round((subtotal + vat) * 100) / 100;
    return { subtotal, vat, total };
  };

  return (
    <div className="border border-[#80C030]/40 bg-[#80C030]/5 rounded-md p-4 mt-3" data-testid="billing-preview">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-[#0D5B69]">Billing Preview</p>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          {billMin ? 'Minimum units enforced' : 'Pro-rata, no minimum'} · VAT 15%
        </p>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Live calculation of what {customer.company_name || 'this customer'} would be billed for sample incident durations.
      </p>
      <div className="space-y-3">
        {previews.map(({ svc, rate, minUnits, isOverride }) => (
          <div key={svc.id} className="bg-white border border-slate-200 rounded-md p-3" data-testid={`preview-${svc.code}`}>
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <p className="text-sm font-semibold text-slate-900">{svc.name}</p>
                <p className="text-[11px] text-slate-500">
                  R {rate.toFixed(2)} /{svc.billing_type === 'fixed' ? 'job' : 'hr'}
                  {isOverride && <span className="ml-2 text-[#80C030] font-semibold">· Custom rate</span>}
                  {minUnits > 0 && <span className="ml-2 text-amber-600">· Min {minUnits}{svc.billing_type === 'fixed' ? '' : 'h'}</span>}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-xs">
              {samples.map((h) => {
                const { total } = compute(rate, svc.billing_type, h, minUnits);
                return (
                  <div key={h} className="bg-slate-50 rounded p-2 text-center">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">{h * 60} min</p>
                    <p className="font-mono text-sm font-semibold text-[#0D5B69]">R {total.toFixed(2)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-400 italic mt-3">
        * Cancel-billing window: {customer.cancel_billing_window_minutes ?? 15} min — cancellations inside the window are not billed (spec A4).
      </p>
    </div>
  );
};
